import { AssistantRuntimeProvider, useMessage } from '@assistant-ui/react';
import { AssistantChatTransport, useChatRuntime } from '@assistant-ui/react-ai-sdk';
import {
  AssistantActionBar,
  AssistantMessage,
  BranchPicker,
  Composer,
  Thread,
  ThreadWelcome,
} from '@assistant-ui/react-ui';
import '@assistant-ui/react-ui/styles/index.css';
import { Alert, Button, Flex, Spin, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { MarkdownText } from '@/common/components/MarkdownText';
import { AI_AVATAR } from '@/common/utils/ai-avatar';
import { useThemePreference } from '@/common/theme/ThemeContext';
import { chatSchemaSkill } from '../services/skill';

const DEFAULT_SKILL_CHAT_HEIGHT = '100%';

export const extractSkillChatOutput = (message, isOutputValid) => {
  const content = message?.content || message?.parts || [];
  console.log('extractSkillChatOutput: ', content);
  const outputPart = content.find(
    (part) =>
      (part.toolName === 'validateSkillOutput' || part.type === 'tool-validateSkillOutput') &&
      (part.result?.success || part.output?.success) &&
      (part.result?.output || part.output?.output) &&
      isOutputValid(part.result?.output || part.output?.output),
  );
  if (outputPart) {
    return outputPart.result?.output || outputPart.output?.output;
  }

  return null;
};

const SkillReasoningMessage = ({ status }) => {
  const isRunning = status?.type === 'running' || status?.type === 'reasoning';
  if (!isRunning) {
    return null;
  }

  return (
    <Spin size="small">
      <Typography.Text type="secondary">思考中...</Typography.Text>
    </Spin>
  );
};

SkillReasoningMessage.propTypes = {
  status: PropTypes.shape({
    type: PropTypes.string,
  }),
};

const SkillAssistantMessage = ({ isOutputValid, onConfirm }) => {
  const currentOutput = useMessage((message) => extractSkillChatOutput(message, isOutputValid));

  return (
    <AssistantMessage.Root>
      <AssistantMessage.Avatar />
      <AssistantMessage.Content
        components={{
          Reasoning: SkillReasoningMessage,
        }}
      />
      <Flex vertical gap="small" style={{ gridColumn: 'span 2 / span 2' }}>
        {currentOutput ? (
          <Alert
            showIcon
            type="success"
            title="结果已生成，请确认使用"
            action={
              <Button size="small" type="primary" onClick={() => onConfirm(currentOutput)}>
                确认使用
              </Button>
            }
          />
        ) : null}
        <BranchPicker />
        <AssistantActionBar />
      </Flex>
    </AssistantMessage.Root>
  );
};

SkillAssistantMessage.propTypes = {
  isOutputValid: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default function SkillChat({
  height = DEFAULT_SKILL_CHAT_HEIGHT,
  input = {},
  isOutputValid,
  onComplete,
  skillName,
  startPrompt,
  strings,
}) {
  const { organizationId } = useOrganizationPath();
  const { themeName } = useThemePreference();

  const api = useMemo(() => {
    return chatSchemaSkill({ organizationId, skillName });
  }, [organizationId, skillName]);
  const startedRef = useRef(false);
  const handleConfirm = useCallback((output) => onComplete?.(output), [onComplete]);
  const SkillAssistantMessageWithConfirm = useCallback(
    () => <SkillAssistantMessage isOutputValid={isOutputValid} onConfirm={handleConfirm} />,
    [handleConfirm, isOutputValid],
  );

  const runtime = useChatRuntime({
    transport: new AssistantChatTransport({
      api,
      body: { input },
      credentials: 'include',
    }),
  });
  const threadConfig = useMemo(
    () => ({
      assistantAvatar: AI_AVATAR,
      composer: { allowAttachments: false },
      components: {
        AssistantMessage: SkillAssistantMessageWithConfirm,
      },
      assistantMessage: {
        components: {
          Empty: SkillReasoningMessage,
          Text: MarkdownText,
        },
      },
      strings: {
        composer: {
          input: {
            placeholder: '请输入...',
          },
        },
        ...strings,
      },
    }),
    [SkillAssistantMessageWithConfirm, strings],
  );

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    runtime.thread.append({
      role: 'user',
      content: [{ type: 'text', text: startPrompt }],
    });
  }, [runtime, startPrompt]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <Flex
        vertical
        style={{
          height,
          maxHeight: height,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Thread.Root className={themeName === 'dark' ? 'dark' : ''} config={threadConfig}>
          <Thread.Viewport>
            <ThreadWelcome />
            <Thread.Messages
              components={{
                AssistantMessage: SkillAssistantMessageWithConfirm,
              }}
            />
            <Thread.FollowupSuggestions />
            <Thread.ViewportFooter>
              <Thread.ScrollToBottom />
              <Composer />
            </Thread.ViewportFooter>
          </Thread.Viewport>
        </Thread.Root>
      </Flex>
    </AssistantRuntimeProvider>
  );
}

SkillChat.propTypes = {
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  input: PropTypes.shape({}),
  isOutputValid: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  skillName: PropTypes.string.isRequired,
  startPrompt: PropTypes.string.isRequired,
  strings: PropTypes.shape({
    composer: PropTypes.shape({
      input: PropTypes.shape({
        placeholder: PropTypes.string,
      }),
    }),
  }),
};
